import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { useFilterSet } from 'hooks/useFilterSet';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { AdvancedFilterData } from '../FilterFormController';
import { FormFilterList } from '../parts/FormFilterList';
import { FilterChip } from '../parts/FilterChip';
import { FilterDialogProps } from '../types';
import { DIALOG_STEPS } from './constants';
import { trackMatomoEvent } from 'services/matomo.service';
import { Button } from 'components/Button';

type SaveDialogProps = FilterDialogProps;

export function SaveDialog({ open, onClose, setDialog }: SaveDialogProps) {
  const [filterSetName, setFilterSetName] = useState('');
  const [selectedFilterSetId, setSelectedFilterSetId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const { filterSets, saveFilterSet, updateFilterSet } = useFilterSet();
  const formContext = useFormContext<AdvancedFilterData>();
  const intl = useIntl();

  function handleErrorTranslation(error: string): string {
    if (error.includes(':')) {
      const [errorKey, name] = error.split(':');
      return intl.formatMessage(
        {
          id: errorKey,
          defaultMessage: 'A filter set with the same filter settings already exists: "{name}"',
        },
        { name }
      );
    }

    return intl.formatMessage({
      id: error,
      defaultMessage: 'An error occurred while processing the filter set.',
    });
  }

  // Initialize dialog - only once when dialog opens
  useEffect(() => {
    if (open && !initialized) {
      setFilterSetName(''); // Clear for new filter
      setSelectedFilterSetId(null);
      setInitialized(true);
    } else if (!open) {
      // Dialog closed - reset for next opening
      setInitialized(false);
    }
  }, [open, initialized]);

  const handleApply = async () => {
    trackMatomoEvent({ category: 'SaveFilters', action: 'Click', name: 'SaveFilter' });

    const filterValues = formContext.getValues();

    if (selectedFilterSetId) {
      const result = await updateFilterSet(
        selectedFilterSetId,
        filterSets.find(set => set.filterSetId === selectedFilterSetId)?.name ?? '',
        filterValues
      );
      if (!result.success) {
        const errorMessage = result.error
          ? handleErrorTranslation(result.error)
          : intl.formatMessage({
              id: 'advanced-filter-error-updating',
              defaultMessage: 'Error updating the filter set.',
            });
        toast.error(errorMessage);
        return;
      }
      const newFilterSetId = result.filterSet?.filterSetId ?? null;
      formContext.setValue('formSelectedFilterSet', newFilterSetId);
      formContext.setValue('formLastClickedFilterSet', newFilterSetId);
    } else if (filterSetName.trim()) {
      const result = await saveFilterSet(filterSetName.trim(), filterValues);

      if (!result.success) {
        const errorMessage = result.error
          ? handleErrorTranslation(result.error)
          : intl.formatMessage({
              id: 'advanced-filter-error-saving',
              defaultMessage: 'Error saving the filter set.',
            });
        toast.error(errorMessage);
        return;
      }
      const newFilterSetId = result.filterSet?.filterSetId ?? null;
      formContext.setValue('formSelectedFilterSet', newFilterSetId);
      formContext.setValue('formLastClickedFilterSet', newFilterSetId);
    }

    setFilterSetName('');
    setSelectedFilterSetId(null);
    setDialog(DIALOG_STEPS.FILTER_SELECT);
  };

  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick') return;
    setDialog(DIALOG_STEPS.FILTER_SELECT);
  };

  const handleFilterChipClick = (id: string) => {
    const isCurrentlySelected = selectedFilterSetId === id;
    const newSelectedId = isCurrentlySelected ? null : id;

    setSelectedFilterSetId(newSelectedId);

    if (!isCurrentlySelected && newSelectedId) {
      setFilterSetName(''); // Reset name only when selecting an existing filter
    }
  };

  const showOverwriteSection = filterSets.length > 0;

  const canSave = filterSetName.trim() || selectedFilterSetId;
  const isNameFieldDisabled = Boolean(selectedFilterSetId);
  const isMaxFilterSetsReached = filterSets.length >= 10;

  const dialogTitle = intl.formatMessage({
    id: 'advanced-filter-set-save-as',
    defaultMessage: 'Save filter set as ...',
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle className="mb-4">
        {dialogTitle}
        <Button
          icon="Cross"
          aria-label="close"
          onClick={() => {
            setDialog(DIALOG_STEPS.FILTER_SELECT);
            onClose();
          }}
          className="ml-auto"
        />
      </DialogTitle>
      <DialogContent>
        <div>
          <Typography variant="h4" sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            Name
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={filterSetName}
            onChange={e => setFilterSetName(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'advanced-filter-set-name',
              defaultMessage: 'Filter set name',
            })}
            disabled={isNameFieldDisabled}
            className="mt-4"
            inputProps={{ maxLength: 50 }}
          />

          <div className="flex justify-end mt-1">
            <span className="text-xs text-gray-500">
              {filterSetName.length}/50 {intl.formatMessage({ id: 'general.math.chars', defaultMessage: 'chars' })}
            </span>
          </div>

          {showOverwriteSection && (
            <div>
              <Typography variant="h4" sx={{ fontSize: '0.75rem', fontWeight: 300, pb: 1 }}>
                <FormattedMessage
                  id="advanced-filter-save-overwrite-option"
                  defaultMessage="Or overwrite existing filter set"
                />
              </Typography>

              <div className="flex gap-1 flex-wrap">
                {filterSets.map(filterSet => (
                  <FilterChip
                    key={filterSet.filterSetId}
                    name={filterSet.name}
                    label={filterSet.name}
                    active={selectedFilterSetId === filterSet.filterSetId}
                    onClick={() => handleFilterChipClick(filterSet.filterSetId)}
                    title={filterSet.name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <FormFilterList formContext={formContext} />
      </DialogContent>
      <DialogActions>
        <Button
          id="save-filter-set-cancel-button"
          priority="secondary"
          onClick={() => setDialog(DIALOG_STEPS.FILTER_SELECT)}
        >
          <FormattedMessage id="general.button.back" defaultMessage="Go back" />
        </Button>
        <Button
          id="save-filter-set-apply-button"
          type="submit"
          onClick={handleApply}
          disabled={!canSave || (isMaxFilterSetsReached && !selectedFilterSetId)}
        >
          <FormattedMessage id="advanced-filter-set-save" defaultMessage="Save filter set" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
